import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Receipt,
  CreditCard,
  Wallet,
  Sparkles,
  Settings,
  Users,
  Shield,
  X,
  Check,
  MoreHorizontal,
  ExternalLink,
} from 'lucide-react';
import type { Notification, NotificationCategory } from '../types/notification.types';
import { CATEGORY_INFO, PRIORITY_INFO } from '../types/notification.types';

// ============================================================================
// Types
// ============================================================================

export interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onClose?: () => void;
  compact?: boolean;
}

// ============================================================================
// Helper Components
// ============================================================================

const CategoryIcon: React.FC<{ category?: NotificationCategory; className?: string }> = ({
  category,
  className = '',
}) => {
  const iconMap: Record<NotificationCategory, React.ElementType> = {
    FISCAL: FileText,
    BILLING: Receipt,
    PAYMENTS: CreditCard,
    TREASURY: Wallet,
    AI_INSIGHTS: Sparkles,
    SYSTEM: Settings,
    COLLABORATION: Users,
    SECURITY: Shield,
  };

  const Icon = category ? (iconMap[category] || Settings) : Settings;
  const info = category && CATEGORY_INFO[category] ? CATEGORY_INFO[category] : { bgColor: 'bg-gray-100', color: 'text-gray-600' };

  return (
    <div className={`p-2 rounded-xl ${info.bgColor} ${className}`}>
      <Icon className={`w-4 h-4 ${info.color}`} />
    </div>
  );
};

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffHour < 24) return `Il y a ${diffHour}h`;
  if (diffDay < 7) return `Il y a ${diffDay}j`;

  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
};

// ============================================================================
// Component
// ============================================================================

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDismiss,
  onClose,
  compact = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showActions, setShowActions] = React.useState(false);

  const handleClick = () => {
    // Mark as read if not already
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    // Navigate if link exists
    if (notification.link) {
      navigate(notification.link);
      onClose?.();
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsRead?.(notification.id);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss?.(notification.id);
  };

  const priorityInfo = PRIORITY_INFO[notification.priority];

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      className={`
        relative flex gap-3 p-3 rounded-xl cursor-pointer
        transition-all duration-200
        ${notification.isRead
          ? 'bg-white/30 hover:bg-white/50'
          : 'bg-white/60 hover:bg-white/80 shadow-sm'
        }
        ${notification.priority === 'URGENT' ? 'border-l-4 border-red-500' : ''}
        ${notification.priority === 'HIGH' ? 'border-l-4 border-orange-400' : ''}
      `}
    >
      {/* Unread Indicator */}
      {!notification.isRead && (
        <div className="absolute top-4 left-1 w-2 h-2 rounded-full bg-teal-500" />
      )}

      {/* Icon */}
      <CategoryIcon category={notification.category} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <h4
            className={`
              text-sm font-semibold text-gray-900
              ${compact ? 'line-clamp-1' : ''}
            `}
          >
            {notification.title}
          </h4>

          {/* AI Badge */}
          {notification.aiGenerated && (
            <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 rounded-full">
              IA
            </span>
          )}
        </div>

        {/* Message */}
        <p
          className={`
            text-sm text-gray-600 mt-0.5
            ${compact ? 'line-clamp-1' : 'line-clamp-2'}
          `}
        >
          {notification.message}
        </p>

        {/* Footer */}
        <div className="flex items-center gap-2 mt-2">
          {/* Time */}
          <span className="text-xs text-gray-400">
            {formatRelativeTime(notification.createdAt)}
          </span>

          {/* Category Label */}
          {notification.category && CATEGORY_INFO[notification.category] && (
            <span className="text-xs text-gray-400">
              {CATEGORY_INFO[notification.category].label}
            </span>
          )}

          {/* Priority Badge (if high/urgent) */}
          {(notification.priority === 'HIGH' || notification.priority === 'URGENT') && (
            <span className={`text-xs font-medium ${priorityInfo.color}`}>
              {priorityInfo.label}
            </span>
          )}
        </div>

        {/* Action Buttons (if defined) */}
        {!compact && notification.actionButtons && notification.actionButtons.length > 0 && (
          <div className="flex gap-2 mt-3">
            {notification.actionButtons.slice(0, 2).map((button, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  if (button.action === 'navigate' && button.url) {
                    navigate(button.url);
                    onClose?.();
                  } else if (button.action === 'external_link' && button.url) {
                    window.open(button.url, '_blank');
                  }
                }}
                className={`
                  px-3 py-1.5 text-xs font-medium rounded-lg
                  transition-colors duration-200
                  ${button.style === 'primary'
                    ? 'bg-teal-600 text-white hover:bg-teal-700'
                    : button.style === 'danger'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {button.label}
                {button.action === 'external_link' && (
                  <ExternalLink className="inline-block w-3 h-3 ml-1" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions (on hover) */}
      {showActions && (
        <div className="absolute top-2 right-2 flex gap-1">
          {!notification.isRead && (
            <button
              onClick={handleMarkAsRead}
              className="p-1.5 rounded-lg bg-white/80 hover:bg-teal-50 text-gray-500 hover:text-teal-600 transition-colors"
              title={t('common.notifications.markAsRead')}
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-lg bg-white/80 hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"
            title={t('common.notifications.hide')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationItem;
